"use strict";
/** Modular and reusable functionality for search elements */
class ThemeSearch {
    constructor(element) {
        // Store elements that are used frequently
        this.element = element;
        this.inputElement = this.element.querySelector('[name="q"]');
        this.resultsElement = this.element.querySelector('[data-search-results="element"]');
        this.resultsContainer = this.element.querySelector('[data-search-results="container"]');
        this.allElement = this.element.querySelector('[data-search-results="all"]');
        this.allElementProducts = this.allElement.querySelector('[data-search-element="products"][data-search-count="1"]');
        this.allElementCollections = this.allElement.querySelector('[data-search-element="collections"]');
        this.allElementOther = this.allElement.querySelector('[data-search-element="other"][data-search-count="1"]');
        this.singleElement = this.element.querySelector('[data-search-results="single"]');
        this.singleElementCollections = this.singleElement.querySelector('[data-search-element="collections"]');
        this.emptyElement = this.element.querySelector('[data-search-results="empty"]');
        this.containerElement = this.element.querySelector('[data-search-element="container"]');
        this.controlsContainer = this.element.querySelector('.SearchResults__CategoryList');
        this.filterContainer = this.element.querySelector('[data-search-element="filter-container"]');
        this.resetButton = this.element.querySelector('[data-action="reset-search"]');
        this.hiddenInput = this.element.querySelector('[data-search-element="hidden-input"]');
        // Init settings and state of search
        this.settings = JSON.parse(element.getAttribute('data-theme-search'));
        this.source = this.settings.source || 'widget';
        this.size = this.settings.size || 'compact';
        this.lastInputValue = '';
        this.activeSelection = null;
        this.activeFilters = new Map();
        this.sortBy = null;
        this.resultData = null;
        this.cache = { collections: null };
        this.abortController = new AbortController();
        const currentBlog = this.getCurrentBlog();
        if (currentBlog !== null) {
            const categoryButton = this.controlsContainer.querySelector(`[data-search-category="${currentBlog}"]`);
            if (categoryButton) {
                this.activeSelection = currentBlog;
                categoryButton.classList.add('is-active');
            }
        }
        this.attachListeners();
        this.initFilters();
        // If results already exist (e.g. on search results page), render them
        if (this.source === 'page' && this.settings.performed === true) {
            this.inputElement.focus();
            this.inputElement.selectionStart = this.inputElement.selectionEnd = 100;
            this.inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            this.toggleLoadingState(true);
            const search = new URLSearchParams(window.location.search);
            this.lastInputValue = search.get('q');
            if (search.has('tab')) {
                const button = this.controlsContainer.querySelector(`[data-search-category="${search.get('tab')}"]`);
                if (button) {
                    this.activeSelection = search.get('tab');
                    this.controlsContainer.querySelector(`[data-search-category].is-active`).classList.remove('is-active');
                    button.classList.add('is-active');
                }
            }
            const queryOptions = {
                method: 'GET',
                credentials: 'same-origin',
                signal: this.abortController.signal
            };
            fetch(`${window.routes.predictiveSearchUrl}?section_id=predictive-search-results&q=${encodeURIComponent(this.lastInputValue)}`, queryOptions).then(response => response.text()).then((html) => {
                this.resultsContainer.innerHTML += html;
                this.processResults();
                this.toggleLoadingState(false);
            }).catch(() => { });
        }
    }
    /** Check if the current page is in a blog that is supported by the search */
    getCurrentBlog() {
        if (window.theme.search.mode.includes('article') && location.pathname.includes('/blogs')) {
            // replace the regular expression of /(?<=blogs\/)[^\/]*/ without lookbehind to support Safari
            const blog = location.pathname.match(/.*blogs\/([^\/]*)/);
            // value of blog is a tuple, the first element is the whole match, the second element is the first capture group, e.g. [ "blogs/magazin", "magazin" ]
            if (blog !== null && window.theme.search.blogs.includes(blog[1]))
                return blog[1];
        }
        return null;
    }
    /** Attach general listeners to the search element */
    attachListeners() {
        this.fetchOnInput = this.debounce(this.fetchResults.bind(this), 325);
        this.abortController = new AbortController();
        this.inputElement.addEventListener('keydown', this.onSubmission.bind(this));
        this.inputElement.addEventListener('input', this.onInput.bind(this));
        this.resetButton.addEventListener('click', () => {
            this.inputElement.value = '';
            this.inputElement.focus();
            this.inputElement.selectionStart = this.inputElement.selectionEnd = 100;
            this.resetSearch();
        });
        this.element.querySelectorAll('[data-search-action="page"]').forEach((button) => {
            button.addEventListener('click', this.openResultsPage.bind(this));
        });
        this.controlsContainer.querySelectorAll('.SearchResults__CategoryListItem').forEach((button) => {
            const category = button.getAttribute('data-search-category');
            if (this.activeSelection === null) {
                button.classList.add('is-active');
                this.activeSelection = category;
            }
            if (!['all'].includes(category))
                this.cache[category] = null;
            button.addEventListener('click', (e) => {
                const target = e.target;
                if (target.classList.contains('is-active'))
                    return;
                this.controlsContainer.querySelector('.SearchResults__CategoryListItem.is-active').classList.remove('is-active');
                target.classList.add('is-active');
                this.activeSelection = target.getAttribute('data-search-category');
                if (this.source === 'page') {
                    const url = new URL(window.location.href);
                    url.searchParams.set('tab', this.activeSelection);
                    history.replaceState({ tab: this.activeSelection }, "", url.toString());
                }
                this.changeSelection();
                this.displayResults();
            });
        });
    }
    /** Initialize product filters if present */
    initFilters() {
        const _this = this;
        // Set multi to true when a filter can have multiple values at the same time
        const addFilter = (name, value, multi) => {
            const sValue = `${value}`;
            if (multi && _this.activeFilters.has(name)) {
                if (_this.activeFilters.get(name) !== sValue) {
                    _this.activeFilters.set(name, `${_this.activeFilters.get(name)},${sValue}`);
                }
            }
            else {
                _this.activeFilters.set(name, sValue);
            }
        };
        const removeFilter = (name, value, multi) => {
            const sValue = `${value}`;
            if (!multi) {
                _this.activeFilters.delete(name);
            }
            else if (_this.activeFilters.has(name) && _this.activeFilters.get(name).includes(sValue)) {
                const values = _this.activeFilters.get(name).split(',');
                if (values.length === 1) {
                    _this.activeFilters.delete(name);
                }
                else {
                    values.splice(values.indexOf(sValue), 1);
                    _this.activeFilters.set(name, values.join(','));
                }
            }
        };
        if (!this.filterContainer)
            return;
        this.filterContainer.querySelectorAll('[data-search-action="filter"]').forEach((filter) => {
            filter.addEventListener('change', (e) => {
                const input = e.target;
                if (input.type === 'checkbox') {
                    const action = input.checked ? addFilter : removeFilter;
                    action(input.name, +input.value, true);
                }
                else if (input.type === 'number') {
                    const action = ['', input.min, input.max].includes(input.value) ? removeFilter : addFilter;
                    action(input.name, +input.value, false);
                }
                this.inputElement.dispatchEvent(new Event('input'));
            });
        });
        this.filterContainer.querySelector('[data-search-action="sort"]').addEventListener('change', (e) => {
            const target = e.target;
            this.sortBy = target.value;
            this.inputElement.dispatchEvent(new Event('input'));
        });
    }
    /** Ensure a function is executed only once in the specified timeframe */
    debounce(fn, delay) {
        const _this = this;
        let timer = null;
        return (...args) => {
            if (timer) {
                clearTimeout(timer);
            }
            timer = setTimeout(() => {
                fn.apply(_this, args);
            }, delay);
        };
    }
    /** Set the loading state of the search */
    toggleLoadingState(isLoading) {
        if (isLoading) {
            this.containerElement.classList.add('loading');
            document.dispatchEvent(new CustomEvent('theme:loading:start'));
        }
        else {
            this.containerElement.classList.remove('loading');
            document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
    }
    /** Helper function to set the visibility of multiple elements and once */
    setElementVisibility(show, hide) {
        if (show)
            (show instanceof Array) ? show.forEach((element) => { element.classList.remove('none'); }) : show.classList.remove('none');
        if (hide)
            (hide instanceof Array) ? hide.forEach((element) => { element.classList.add('none'); }) : hide.classList.add('none');
    }
    /** Open search results page with parameters depending on the current input and tab selection */
    openResultsPage(e) {
        const target = e.target;
        let href = window.routes.searchUrl;
        if (this.lastInputValue !== '') {
            href += `?q=${this.lastInputValue}`;
            let tab = this.activeSelection;
            if (e) {
                const button = target.getAttribute('data-search-action') === 'page' ? target : target.closest('[data-search-action="page"]');
                if (button && button.hasAttribute('data-search-tab'))
                    tab = button.getAttribute('data-search-tab');
            }
            if (tab === 'all' || tab === 'no-products') {
                const availableTabs = Array.from(this.controlsContainer.querySelectorAll('[data-search-category]'));
                let tempTab = null;
                if (this.resultData) {
                    for (let i = 0; i < availableTabs.length; i++) {
                        const category = availableTabs[i].getAttribute('data-search-category');
                        if (category === 'all') {
                            continue;
                        }
                        else if (category === 'products' && tab !== 'no-products') {
                            if (this.resultData[category].length > 0 || (window.theme.search.mode.includes('collection') && this.resultData['collections'].length > 0)) {
                                tempTab = category;
                                break;
                            }
                        }
                        else if (category === 'pages') {
                            if (this.resultData[category].length > 0) {
                                tempTab = category;
                                break;
                            }
                        }
                        else { // Articles need special handling since the tabs depend on the blogs
                            const blogArticleCount = this.resultData.articles.reduce((acc, curr) => {
                                if (curr.blog === category)
                                    acc++;
                                return acc;
                            }, 0);
                            if (blogArticleCount > 0) {
                                tempTab = category;
                                break;
                            }
                        }
                    }
                }
                if (tempTab !== null)
                    href += `&tab=${tempTab}`;
            }
            else {
                href += `&tab=${tab}`;
            }
        }
        window.location.href = href;
    }
    /** Encode filter options to use them for fetching new results */
    encodeFilters() {
        const result = [];
        this.activeFilters.forEach((value, key) => {
            if (value.includes(',')) {
                const arr = value.split(',');
                for (let i = 0; i < arr.length; i++) {
                    arr[i] = encodeURIComponent(arr[i]);
                }
                result.push(`${key}=${arr.join(',')}`);
                return;
            }
            result.push(`${key}=${encodeURIComponent(value)}`);
        });
        return result.join('&');
    }
    /** Redirect to search page on submission in widget */
    onSubmission(event) {
        if (event.keyCode === 13) {
            event.preventDefault();
        }
    }
    /** Actions on user input without fetching new results instantly */
    onInput(e) {
        const event = e;
        if (event.keyCode === 13) {
            return;
        }
        this.toggleLoadingState(true);
        // Abort currently running requests since they are not needed anymore
        this.abortController.abort();
        this.abortController = new AbortController();
        const target = event.target;
        this.lastInputValue = target.value;
        if (this.source === 'page') {
            const url = new URL(window.location.href);
            url.searchParams.set('q', this.lastInputValue);
            history.replaceState({ q: this.lastInputValue }, "", url.toString());
        }
        if (this.lastInputValue === '') {
            this.resetSearch();
            return;
        }
        this.hiddenInput.innerText = this.lastInputValue.replace(/\s/g, '.');
        this.resetButton.style.setProperty('--input-width', `${this.hiddenInput.clientWidth}px`);
        this.resetButton.classList.remove('none');
        this.resultsElement.setAttribute('aria-hidden', 'false');
        this.fetchOnInput(target.value);
    }
    /** Resets the search when no query is present */
    resetSearch() {
        this.resultsContainer.innerHTML = '';
        this.resultsElement.setAttribute('aria-hidden', 'true');
        this.resetButton.classList.add('none');
        this.toggleLoadingState(false);
    }
    /** Fetch search results for the given query and possibly active filters and sorting options */
    fetchResults(q) {
        const _this = this;
        const queryOptions = {
            method: 'GET',
            credentials: 'same-origin',
            signal: this.abortController.signal
        };
        const queries = [fetch(`${window.routes.predictiveSearchUrl}?section_id=predictive-search-results&q=${encodeURIComponent(q)}`, queryOptions),
            fetch(`${window.routes.searchUrl}?section_id=search-results&options[unavailable_products]=show&${this.sortBy ? `sort_by=${this.sortBy}&` : ''}q=${encodeURIComponent(q)}`, queryOptions)];
        if (this.activeFilters.size > 0)
            queries.push(fetch(`${window.routes.searchUrl}?section_id=search-results&${this.sortBy ? `sort_by=${this.sortBy}&` : ''}type=product&options[unavailable_products]=show&${this.encodeFilters()}&q=${encodeURIComponent(q)}`, queryOptions));
        Promise.all(queries).then((responses) => {
            Promise.all(responses.map((response) => {
                return response.text();
            })).then((contents) => {
                _this.resultsContainer.innerHTML = contents.join('');
                _this.processResults();
            }).finally(() => {
                _this.toggleLoadingState(false);
            });
        }).catch(() => { });
    }
    /** Resets elements and data after new search results were fetched */
    processResults() {
        this.resultData = JSON.parse(this.resultsContainer.querySelector(`[data-search-response="default"]`).innerText);
        const predictiveResult = JSON.parse(this.resultsContainer.querySelector(`[data-search-response="predictive"]`).innerText);
        this.resultData.collections = predictiveResult.collections;
        const productResult = this.resultsContainer.querySelector(`[data-search-response="default-filtered"]`);
        if (productResult)
            this.resultData.products = JSON.parse(productResult.innerText).products;
        this.allElementProducts.querySelector('[data-search-element="content"]').innerHTML = '';
        this.allElementCollections.querySelector('[data-search-element="collections-content"]').innerHTML = '';
        this.allElementOther.querySelector('[data-search-element="content"]').innerHTML = '';
        this.allElement.setAttribute('data-initialized', 'false');
        this.singleElement.querySelector('[data-search-element="content"]').innerHTML = '';
        this.singleElementCollections.querySelector('[data-search-element="collections-content"]').innerHTML = '';
        this.setElementVisibility(null, [this.allElementCollections, this.singleElementCollections]);
        this.allElement.querySelectorAll('[data-search-count="0"]').forEach((element) => {
            this.setElementVisibility(null, element);
        });
        this.allElement.querySelectorAll('[data-search-count="1"]').forEach((element) => {
            this.setElementVisibility(element);
        });
        Object.keys(this.cache).forEach((key) => {
            this.cache[key] = null;
        });
        this.changeSelection();
        this.displayResults();
    }
    /** Changes the displayed category */
    changeSelection() {
        switch (this.activeSelection) {
            case 'all': {
                if (this.filterContainer)
                    this.setElementVisibility(null, this.filterContainer);
                this.setElementVisibility(this.allElement, this.singleElement);
                break;
            }
            case 'products': {
                if (this.filterContainer)
                    this.setElementVisibility(this.filterContainer);
                this.singleElement.querySelector('[data-search-element="content"]').innerHTML = '';
                this.singleElement.classList.remove('SearchResults__Segment--other');
                this.singleElement.classList.add('SearchResults__Segment--products');
                this.setElementVisibility(this.singleElement, this.allElement);
                break;
            }
            case 'pages': {
                if (this.filterContainer)
                    this.setElementVisibility(null, this.filterContainer);
                this.singleElement.querySelector('[data-search-element="content"]').innerHTML = '';
                this.singleElement.classList.add('SearchResults__Segment--other');
                this.singleElement.classList.remove('SearchResults__Segment--products');
                this.setElementVisibility(this.singleElement, this.allElement);
                break;
            }
            default: {
                if (this.filterContainer)
                    this.setElementVisibility(null, this.filterContainer);
                this.singleElement.querySelector('[data-search-element="content"]').innerHTML = '';
                this.singleElement.classList.add('SearchResults__Segment--other');
                this.singleElement.classList.remove('SearchResults__Segment--products');
                this.setElementVisibility(this.singleElement, this.allElement);
            }
        }
    }
    /** Displays the search results for the selected category */
    displayResults() {
        this.setElementVisibility(null, this.singleElementCollections);
        switch (this.activeSelection) {
            case 'all': {
                // When no results, show empty container
                if (this.resultData.totalCount === 0) {
                    this.setElementVisibility(this.emptyElement, this.allElement);
                }
                else {
                    this.setElementVisibility(this.allElement, this.emptyElement);
                    // When element is not already initialized after a search action, initialize it
                    if (this.allElement.getAttribute('data-initialized') === 'false') {
                        if (window.theme.search.mode.includes('product')) {
                            if (this.resultData.products.length === 0) {
                                this.setElementVisibility(this.allElement.querySelector('[data-search-element="products"][data-search-count="0"]'), this.allElementProducts);
                            }
                            else {
                                let productsHtml = '';
                                for (let i = 0; i < (this.size === 'full' ? this.resultData.products.length : Math.min(this.resultData.products.length, window.theme.search.maxAll)); i++) {
                                    productsHtml += this.resultData.products[i];
                                }
                                this.allElementProducts.querySelector('[data-search-element="content"]').innerHTML = productsHtml;
                                // Toggle show all button visibility
                                if (this.size !== 'full' && this.resultData.products.length > window.theme.search.maxAll) {
                                    this.setElementVisibility(this.allElementProducts.querySelector('[data-search-action="page"]'));
                                }
                                else {
                                    this.setElementVisibility(null, this.allElementProducts.querySelector('[data-search-action="page"]'));
                                }
                                document.dispatchEvent(new CustomEvent('theme:search:changed', { detail: this.element }));
                            }
                        }
                        else {
                            this.setElementVisibility(null, this.allElementProducts);
                        }
                        if (window.theme.search.mode.includes('collection') && this.resultData.collections && this.resultData.collections.length > 0) {
                            const collectionsContent = this.allElementCollections.querySelector('[data-search-element="collections-content"]');
                            if (collectionsContent.innerHTML === '') {
                                if (this.cache.collections === null)
                                    this.cache.collections = this.resultData.collections.join('');
                                this.allElementCollections.querySelector('[data-search-element="collections-content"]').innerHTML = this.cache.collections;
                                this.allElementCollections.dispatchEvent(new CustomEvent('theme:hsl:refresh'));
                            }
                            this.setElementVisibility([this.allElementProducts, this.allElementCollections], this.allElement.querySelector('[data-search-element="products"][data-search-count="0"]'));
                        }
                        const otherArr = [];
                        if (window.theme.search.mode.includes('article')) {
                            this.resultData.articles.forEach((article) => {
                                otherArr.push(article.html);
                            });
                        }
                        if (window.theme.search.mode.includes('page'))
                            otherArr.push(...this.resultData.pages);
                        if (otherArr.length === 0) {
                            this.setElementVisibility(this.allElement.querySelector('[data-search-element="other"][data-search-count="0"]'), this.allElementOther);
                        }
                        else {
                            let otherHtml = '';
                            for (let i = 0; i < (this.size === 'full' ? otherArr.length : Math.min(otherArr.length, window.theme.search.maxAll)); i++) {
                                otherHtml += otherArr[i];
                            }
                            this.allElementOther.querySelector('[data-search-element="content"]').innerHTML = otherHtml;
                            // Toggle show all button visibility
                            if (this.size !== 'full' && otherArr.length > window.theme.search.maxAll) {
                                this.setElementVisibility(this.allElementOther.querySelector('[data-search-action="page"]'));
                            }
                            else {
                                this.setElementVisibility(null, this.allElementOther.querySelector('[data-search-action="page"]'));
                            }
                        }
                        this.allElement.setAttribute('data-initialized', 'true');
                    }
                }
                break;
            }
            case 'products': {
                if (this.resultData.products.length === 0) {
                    this.setElementVisibility(this.emptyElement, this.singleElement);
                }
                else {
                    this.setElementVisibility(this.singleElement, this.emptyElement);
                    if (this.cache.products === null) {
                        let productsHtml = '';
                        for (let i = 0; i < (this.size === 'full' ? this.resultData.products.length : Math.min(this.resultData.products.length, window.theme.search.maxSingle)); i++) {
                            productsHtml += this.resultData.products[i];
                        }
                        this.cache.products = productsHtml;
                    }
                    this.singleElement.querySelector('[data-search-element="content"]').innerHTML = this.cache.products;
                    // Toggle show all button visibility
                    if (this.size !== 'full' && this.resultData.products.length > window.theme.search.maxSingle) {
                        this.setElementVisibility(this.singleElement.querySelector('[data-search-action="page"]'));
                    }
                    else {
                        this.setElementVisibility(null, this.singleElement.querySelector('[data-search-action="page"]'));
                    }
                    document.dispatchEvent(new CustomEvent('theme:search:changed', { detail: this.element }));
                }
                if (window.theme.search.mode.includes('collection') && this.resultData.collections && this.resultData.collections.length > 0) {
                    const collectionsContent = this.singleElementCollections.querySelector('[data-search-element="collections-content"]');
                    if (collectionsContent.innerHTML === '') {
                        if (this.cache.collections === null)
                            this.cache.collections = this.resultData.collections.join('');
                        this.singleElementCollections.querySelector('[data-search-element="collections-content"]').innerHTML = this.cache.collections;
                        this.singleElementCollections.dispatchEvent(new CustomEvent('theme:hsl:refresh'));
                    }
                    this.setElementVisibility([this.singleElementCollections, this.singleElement], this.emptyElement);
                }
                break;
            }
            case 'pages': {
                if (this.resultData.pages.length === 0) {
                    this.setElementVisibility(this.emptyElement, this.singleElement);
                }
                else {
                    this.setElementVisibility(this.singleElement, this.emptyElement);
                    if (this.cache.pages === null) {
                        let pagesHtml = '';
                        for (let i = 0; i < (this.size === 'full' ? this.resultData.pages.length : Math.min(this.resultData.pages.length, window.theme.search.maxSingle)); i++) {
                            pagesHtml += this.resultData.pages[i];
                        }
                        this.cache.pages = pagesHtml;
                    }
                    this.singleElement.querySelector('[data-search-element="content"]').innerHTML = this.cache.pages;
                    // Toggle show all button visibility
                    if (this.size !== 'full' && this.resultData.pages.length > window.theme.search.maxSingle) {
                        this.setElementVisibility(this.singleElement.querySelector('[data-search-action="page"]'));
                    }
                    else {
                        this.setElementVisibility(null, this.singleElement.querySelector('[data-search-action="page"]'));
                    }
                    document.dispatchEvent(new CustomEvent('theme:search:changed', { detail: this.element }));
                }
                break;
            }
            default: {
                const articles = [];
                this.resultData.articles.forEach((article) => {
                    if (article.blog === this.activeSelection)
                        articles.push(article.html);
                });
                if (articles.length === 0) {
                    this.setElementVisibility(this.emptyElement, this.singleElement);
                }
                else {
                    this.setElementVisibility(this.singleElement, this.emptyElement);
                    if (this.cache[this.activeSelection] === null) {
                        let articlesHtml = '';
                        for (let i = 0; i < (this.size === 'full' ? articles.length : Math.min(articles.length, window.theme.search.maxSingle)); i++) {
                            articlesHtml += articles[i];
                        }
                        this.cache[this.activeSelection] = articlesHtml;
                    }
                    this.singleElement.querySelector('[data-search-element="content"]').innerHTML = this.cache[this.activeSelection];
                    // Toggle show all button visibility
                    if (this.size !== 'full' && articles.length > window.theme.search.maxSingle) {
                        this.setElementVisibility(this.singleElement.querySelector('[data-search-action="page"]'));
                    }
                    else {
                        this.setElementVisibility(null, this.singleElement.querySelector('[data-search-action="page"]'));
                    }
                    document.dispatchEvent(new CustomEvent('theme:search:changed', { detail: this.element }));
                }
            }
        }
    }
}
// Make the search class and instances globally available
window.themeSearch = window.themeSearch || { initialized: false, template: ThemeSearch, instances: {} };
document.querySelectorAll('[data-theme-search]').forEach((element) => {
    //@ts-expect-error
    if (window.themeSearch.instances[element])
        return;
    //@ts-expect-error
    window.themeSearch.instances[element] = new window.themeSearch.template(element);
});
window.themeSearch.initialized = true;
